import useBreakpoint from '@/lib/hooks/useBreakpoint';
import { COLORS } from '@/styles/color';
import { mediaBreakpointUp, paddingX } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { FC, useMemo } from 'react';
import styled, { css } from 'styled-components';

const Wrapper = styled.ul`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${remCalc(5)};
  margin-bottom: ${remCalc(60)};
`;

interface PageLinkProps {
  current: boolean;
  disabled?: boolean;
}

const PageLink = styled.a<PageLinkProps>`
  ${customTextSize(14, 14)}
  ${paddingX(5)}

  align-items: center;
  background-color: ${(props) => (props.current ? COLORS.VIOLET.HIGHLIGHT : 'transparent')};
  border-radius: ${remCalc(6)};
  color: ${COLORS.VIOLET.LOWLIGHT};
  display: inline-flex;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  height: ${remCalc(26)};
  justify-content: center;
  min-width: ${remCalc(26)};
  text-align: center;
  transition: background-color 0.1s ease-in-out;

  &:hover {
    background-color: ${COLORS.VIOLET.HIGHLIGHT};
  }

  ${(props) =>
    props.disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.25;

      &:hover {
        background-color: transparent;
      }
    `}

  ${mediaBreakpointUp('tablet')} {
    ${customTextSize(16, 16)}
    ${paddingX(8)}

    height: ${remCalc(30)};
    min-width: ${remCalc(30)};
  }
`;

interface PaginationProps {
  pages: number;
  currentPage: number;
  padding: number;
  margin: number;
  onPageChange: (page: number) => void;
  hrefBuilder?: (page: number) => string;
}

const Pagination: FC<PaginationProps> = (props) => {
  const { currentPage, margin, onPageChange, padding, pages, hrefBuilder } = props;

  const [currentBreakpoint] = useBreakpoint();

  const allNumbers = useMemo(() => {
    const numbers = [...Array(pages)].map((_, i) => i + 1);
    const left = numbers.slice(0, margin);
    const centerStart = Math.max(0, currentPage - 1 - padding);
    // prettier-ignore
    const center = numbers.slice(centerStart, ((centerStart + padding) * 2) + 1);
    const right = numbers.splice(numbers.length - margin);

    /* Remove the duplicates */
    return [...new Set([...left, ...center, ...right])];
  }, [pages, currentPage, padding, margin]);

  return (
    <Wrapper>
      <li>
        <PageLink
          role="button"
          href={hrefBuilder && currentPage - 1 > 0 ? hrefBuilder(currentPage - 1) : undefined}
          current={false}
          aria-disabled={currentPage === 1}
          disabled={currentPage === 1}
          aria-label={`Go back to page ${currentPage - 1}`}
          onClick={(e) => {
            e.preventDefault();
            if (currentPage > 1) onPageChange(currentPage - 1);
          }}
        >
          {currentBreakpoint === 'mobile' ? '<' : 'Previous'}
        </PageLink>
      </li>

      {allNumbers.map((number, i, numbers) => {
        const nextNumber = numbers[i + 1];
        const jumpNumber: number | null =
          number !== pages && number + 1 !== nextNumber ? number + Math.floor((nextNumber - number) / 2) : null;

        return (
          <>
            <li key={number}>
              <PageLink
                role="button"
                href={hrefBuilder ? hrefBuilder(number) : undefined}
                current={number === currentPage}
                aria-label={`Page number ${number}`}
                onClick={(e) => {
                  e.preventDefault();
                  onPageChange(number);
                }}
              >
                {number}
              </PageLink>
            </li>

            {jumpNumber && (
              <li onClick={() => onPageChange(jumpNumber)}>
                <PageLink
                  role="button"
                  href={hrefBuilder ? hrefBuilder(jumpNumber) : undefined}
                  current={false}
                  aria-label={`Page number ${jumpNumber}`}
                  onClick={(e) => {
                    e.preventDefault();
                    onPageChange(jumpNumber);
                  }}
                >
                  {jumpNumber - 1 === number && jumpNumber + 1 === nextNumber ? number + 1 : '...'}
                </PageLink>
              </li>
            )}
          </>
        );
      })}

      <li>
        <PageLink
          role="button"
          href={hrefBuilder && currentPage + 1 <= pages ? hrefBuilder(currentPage + 1) : undefined}
          current={false}
          aria-disabled={currentPage === pages}
          disabled={currentPage === pages}
          aria-label={`Go forward to page ${currentPage + 1}`}
          onClick={(e) => {
            e.preventDefault();
            if (currentPage < pages) onPageChange(currentPage + 1);
          }}
        >
          {currentBreakpoint === 'mobile' ? '>' : 'Next'}
        </PageLink>
      </li>
    </Wrapper>
  );
};

export default Pagination;
